/**
 * Pure navigation helper for the agent configure page (CLA-697).
 *
 * Kept free of atom / RPC imports so link-out surfaces that must NOT pull the
 * customer-agent atom graph (the reseller roster, the admin agent page) can
 * import it directly. `customer-agent.ts` re-exports it for the dashboard
 * surfaces.
 *
 * @since 1.0.0
 */
/**
 * The dashboard configure page's provider sections (`codex` LLM auth, `channels`,
 * `connectors`). This is a distinct axis from the onboarding Connect sub-steps
 * (CLA-850 renamed those to `codex|services|discord|google|review`): the
 * configure page mounts the intake-free Connect panels grouped by provider kind,
 * so it keeps its own stable section vocabulary.
 *
 * @since 1.0.0
 */
export type ConnectSectionId = "codex" | "channels" | "connectors";

/**
 * The valid configure-section ids as a set, for `?section=` validation and the
 * OAuth-callback origin round-trip.
 *
 * @since 1.0.0
 */
export const CONNECT_SECTION_IDS: ReadonlySet<string> = new Set(["codex", "channels", "connectors"]);

/**
 * Type guard for {@link ConnectSectionId} — validates the configure page's
 * `?section=` search param.
 *
 * @since 1.0.0
 */
export const isConnectSectionId = (value: unknown): value is ConnectSectionId =>
  value === "codex" || value === "channels" || value === "connectors";

/**
 * Link target for one agent's configure page — the intake-free Connect panels
 * mounted at `/dashboard/agents/:id/configure`, optionally deep-linked to a
 * `?section=`. Plain `string` so it flows into `<Link to>` without TanStack's
 * literal-route validation, and so callers can build a computed, interpolated
 * path per agent id.
 *
 * @since 1.0.0
 */
export const agentConfigureHref = (agentId: string, section?: ConnectSectionId): string =>
  section === undefined
    ? `/clawinstall/dashboard/agents/${agentId}/configure`
    : `/clawinstall/dashboard/agents/${agentId}/configure?section=${section}`;
